import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../config/firebase';

export interface ImageUploadResult {
  url: string;
  fileName: string;
  size: number;
}

export class ImageUploadService {
  // Maximum file size (5MB)
  private static readonly MAX_FILE_SIZE = 5 * 1024 * 1024;
  
  // Compression threshold (1MB)
  private static readonly COMPRESSION_THRESHOLD = 1 * 1024 * 1024;
  
  // Allowed image types
  private static readonly ALLOWED_TYPES = [
    'image/jpeg',
    'image/jpg', 
    'image/png',
    'image/gif',
    'image/webp'
  ];

  /**
   * Upload an image file to Firebase Storage with automatic compression
   */
  static async uploadChatImage(file: File, userId: string, groupId: string): Promise<ImageUploadResult> {
    // Validate file
    this.validateFile(file);

    // Compress image if it's larger than 1MB
    let processedFile = file;
    if (file.size > this.COMPRESSION_THRESHOLD) {
      console.log(`🗜️ Compressing image: ${(file.size / 1024 / 1024).toFixed(2)}MB -> targeting 50% reduction`);
      processedFile = await this.compressImageForChat(file);
      console.log(`✅ Image compressed: ${(processedFile.size / 1024 / 1024).toFixed(2)}MB (${((1 - processedFile.size / file.size) * 100).toFixed(1)}% reduction)`);
    }

    // Generate unique filename
    const timestamp = Date.now();
    const fileName = `chat-images/${groupId}/${userId}/${timestamp}-${processedFile.name}`;
    
    // Create storage reference
    const storageRef = ref(storage, fileName);
    
    try {
      // Upload file
      const snapshot = await uploadBytes(storageRef, processedFile);
      
      // Get download URL
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      return {
        url: downloadURL,
        fileName: fileName,
        size: processedFile.size
      };
    } catch (error) {
      console.error('Error uploading image:', error);
      throw new Error('Failed to upload image. Please try again.');
    }
  }

  /**
   * Delete an image from Firebase Storage
   */
  static async deleteImage(fileName: string): Promise<void> {
    try {
      const storageRef = ref(storage, fileName);
      await deleteObject(storageRef);
    } catch (error) {
      console.error('Error deleting image:', error);
      // Don't throw error for delete failures as message was already sent
    }
  }

  /**
   * Validate uploaded file
   */
  private static validateFile(file: File): void {
    // Check file size
    if (file.size > this.MAX_FILE_SIZE) {
      throw new Error('File size must be less than 5MB');
    }

    // Check file type
    if (!this.ALLOWED_TYPES.includes(file.type)) {
      throw new Error('Only JPEG, PNG, GIF, and WebP images are allowed');
    }
  }

  /**
   * Compress image specifically for chat (targets ~50% size reduction for files > 1MB)
   */
  static async compressImageForChat(file: File): Promise<File> {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d')!;
      const img = new Image();
      
      img.onload = () => {
        const originalSize = file.size;
        
        // Calculate compression settings based on file size
        let quality = 0.7; // Start with 70% quality
        let maxDimension = 1200; // Max width/height
        
        // More aggressive compression for larger files
        if (originalSize > 3 * 1024 * 1024) { // > 3MB
          quality = 0.5;
          maxDimension = 1000;
        } else if (originalSize > 2 * 1024 * 1024) { // > 2MB
          quality = 0.6;
          maxDimension = 1100;
        }
        
        // Calculate new dimensions while maintaining aspect ratio
        const ratio = Math.min(maxDimension / img.width, maxDimension / img.height, 1);
        const newWidth = Math.floor(img.width * ratio);
        const newHeight = Math.floor(img.height * ratio);
        
        // Set canvas size
        canvas.width = newWidth;
        canvas.height = newHeight;
        
        // Use better image rendering
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        
        // Draw and compress
        ctx.drawImage(img, 0, 0, newWidth, newHeight);
        
        // Try different output formats for better compression
        const outputType = file.type === 'image/png' ? 'image/jpeg' : file.type;
        
        canvas.toBlob((blob) => {
          if (blob) {
            // Create new filename with compression indicator
            const originalName = file.name;
            const nameWithoutExt = originalName.substring(0, originalName.lastIndexOf('.'));
            const ext = outputType === 'image/jpeg' ? '.jpg' : originalName.substring(originalName.lastIndexOf('.'));
            const newName = `${nameWithoutExt}_compressed${ext}`;
            
            const compressedFile = new File([blob], newName, {
              type: outputType,
              lastModified: Date.now()
            });
            
            // If compression didn't achieve much, try with lower quality
            if (compressedFile.size > originalSize * 0.7 && quality > 0.4) {
              // Recursive compression with lower quality
              canvas.toBlob((secondBlob) => {
                if (secondBlob) {
                  const finalFile = new File([secondBlob], newName, {
                    type: outputType,
                    lastModified: Date.now()
                  });
                  resolve(finalFile);
                } else {
                  resolve(compressedFile);
                }
              }, outputType, quality - 0.2);
            } else {
              resolve(compressedFile);
            }
          } else {
            resolve(file); // Return original if compression fails
          }
        }, outputType, quality);
      };
      
      img.onerror = () => resolve(file); // Return original if loading fails
      img.src = URL.createObjectURL(file);
    });
  }

  /**
   * Compress image if needed (basic implementation) - kept for backward compatibility
   */
  static async compressImage(file: File, maxWidth: number = 800, quality: number = 0.8): Promise<File> {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d')!;
      const img = new Image();
      
      img.onload = () => {
        // Calculate new dimensions
        const ratio = Math.min(maxWidth / img.width, maxWidth / img.height);
        const newWidth = img.width * ratio;
        const newHeight = img.height * ratio;
        
        // Set canvas size
        canvas.width = newWidth;
        canvas.height = newHeight;
        
        // Draw and compress
        ctx.drawImage(img, 0, 0, newWidth, newHeight);
        
        canvas.toBlob((blob) => {
          if (blob) {
            const compressedFile = new File([blob], file.name, {
              type: file.type,
              lastModified: Date.now()
            });
            resolve(compressedFile);
          } else {
            resolve(file); // Return original if compression fails
          }
        }, file.type, quality);
      };
      
      img.onerror = () => resolve(file); // Return original if loading fails
      img.src = URL.createObjectURL(file);
    });
  }

  /**
   * Create a preview URL for a file, with optional compression for large files
   */
  static async createPreviewUrlWithCompression(file: File): Promise<{ url: string; compressedFile?: File }> {
    if (file.size > this.COMPRESSION_THRESHOLD) {
      const compressedFile = await this.compressImageForChat(file);
      return {
        url: URL.createObjectURL(compressedFile),
        compressedFile
      };
    }
    
    return {
      url: URL.createObjectURL(file)
    };
  }

  /**
   * Create a preview URL for a file
   */
  static createPreviewUrl(file: File): string {
    return URL.createObjectURL(file);
  }

  /**
   * Revoke a preview URL to free memory
   */
  static revokePreviewUrl(url: string): void {
    URL.revokeObjectURL(url);
  }
}

export default ImageUploadService; 